// Pre-existing native import - exempt from multi-version OTA adapter layer requirement.
// New usages should be migrated to the adapter layer.
// eslint-disable-next-line no-restricted-imports
import { Diagnostic } from "@ionic-native/diagnostic";
import { APP_V2_USER_EVENTS, isCapacitorPlatform } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import axios from "axios";

import { ClockActionPictureDialogEvent } from "./ClockActionPictureDialogEvent";

async function getMicCameraStatuses() {
  const microphoneStatus = (await Diagnostic.getMicrophoneAuthorizationStatus()) as string;
  const cameraStatus = (await Diagnostic.getCameraAuthorizationStatus(false)) as string;

  return { microphoneStatus, cameraStatus };
}

// TODO(TX-2186): refactor this for reusability
export async function getPermissions(): Promise<{ camera: boolean; microphone: boolean }> {
  if (!isCapacitorPlatform()) {
    return { camera: true, microphone: true };
  }

  const NOT_REQUESTED_PERMISSION_STATUSES = new Set([Diagnostic.permissionStatus.NOT_REQUESTED]);
  const ALLOWED_PERMISSION_STATUSES = new Set([
    Diagnostic.permissionStatus.GRANTED,
    Diagnostic.permissionStatus.GRANTED_WHEN_IN_USE,
  ]);

  let { microphoneStatus, cameraStatus } = await getMicCameraStatuses();

  if (
    ALLOWED_PERMISSION_STATUSES.has(microphoneStatus) &&
    ALLOWED_PERMISSION_STATUSES.has(cameraStatus)
  ) {
    return { camera: true, microphone: true };
  }

  if (NOT_REQUESTED_PERMISSION_STATUSES.has(microphoneStatus)) {
    await Diagnostic.requestMicrophoneAuthorization();
  }

  if (NOT_REQUESTED_PERMISSION_STATUSES.has(cameraStatus)) {
    await Diagnostic.requestCameraAuthorization(true);
  }

  ({ microphoneStatus, cameraStatus } = await getMicCameraStatuses());
  return {
    camera: ALLOWED_PERMISSION_STATUSES.has(cameraStatus),
    microphone: ALLOWED_PERMISSION_STATUSES.has(microphoneStatus),
  };
}

export async function uploadTimeclockProofVideoToS3(props: {
  uploadUrl: string;
  videoBlob: Blob;
  // Provides upload progress in the range of 0 to 1
  progressCallback: (progress: number) => void;
  logData: Record<string, unknown>;
  abortSignal?: AbortSignal;
}): Promise<{ isSuccess: boolean }> {
  const { uploadUrl, videoBlob, progressCallback, logData, abortSignal } = props;
  const uploadStartTime = Date.now();

  try {
    const fetchResponse = await axios.put(uploadUrl, videoBlob, {
      method: "PUT",
      signal: abortSignal,
      onUploadProgress: (progressEvent) => {
        const { loaded, total } = progressEvent;
        if (total) {
          const percentCompleted = loaded / total;
          progressCallback(percentCompleted);
        }
      },
    });

    const statusCodeOk = fetchResponse.status >= 200 && fetchResponse.status < 300;
    if (!statusCodeOk) {
      throw new Error(
        `Failed to upload video to S3: ${fetchResponse.statusText}: ${fetchResponse.data}`
      );
    }

    logEvent(APP_V2_USER_EVENTS.FACILITY_TIMECLOCK_COMPLIANCE_EVENT, {
      ...logData,
      uploadDurationMs: Date.now() - uploadStartTime,
      eventAction: ClockActionPictureDialogEvent.UPLOAD_SUCCESS,
    });
  } catch (error: unknown) {
    // Don't log cancellation as a failure
    if (error instanceof Error && error.name !== "CanceledError") {
      logEvent(APP_V2_USER_EVENTS.FACILITY_TIMECLOCK_COMPLIANCE_EVENT, {
        ...logData,
        uploadDurationMs: Date.now() - uploadStartTime,
        eventAction: ClockActionPictureDialogEvent.UPLOAD_FAILURE,
        source: "s3-upload",
        error: error.message,
      });
    }

    return { isSuccess: false };
  }

  return { isSuccess: true };
}
